/**
 * FailedTablesView Component
 *
 * Displays failed tables grouped by error category with fix recommendations
 */

import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";

interface PipelineRun {
  runId: string;
  pipelineName: string;
  status: string;
  runStart: string;
  parameters?: Record<string, any>;
  lastTableProcessed?: string | null;
  failedActivities?: Array<{
    activityName: string;
    activityType: string;
    tableName?: string;
    error: {
      errorCode: string;
      message: string;
    };
    errorCategory: string;
  }>;
}

interface FailedTablesViewProps {
  runs: PipelineRun[];
  isLoading: boolean;
}

interface FailedTable {
  tableName: string;
  errorCategory: string;
  errorMessage: string;
  runId: string;
  runStart: string;
  recommendedFix: string;
  sqlFix: string;
}

export function FailedTablesView({ runs, isLoading }: FailedTablesViewProps) {
  // Helper function to get recommended fix for error category
  const getRecommendedFix = (category: string): { fix: string; sql: string } => {
    const fixes: Record<string, { fix: string; sql: string }> = {
      VARCHAR_TOO_SMALL: {
        fix: 'Increase VARCHAR column size to 500 characters',
        sql: 'ALTER TABLE [{tableName}] ALTER COLUMN [{columnName}] VARCHAR(500);',
      },
      NVARCHAR_TOO_SMALL: {
        fix: 'Increase NVARCHAR column size to 500 characters',
        sql: 'ALTER TABLE [{tableName}] ALTER COLUMN [{columnName}] NVARCHAR(500);',
      },
      DECIMAL_PRECISION: {
        fix: 'Increase DECIMAL precision to 28',
        sql: 'ALTER TABLE [{tableName}] ALTER COLUMN [{columnName}] DECIMAL(28, {scale});',
      },
      TINYINT_OVERFLOW: {
        fix: 'Change TINYINT to SMALLINT',
        sql: 'ALTER TABLE [{tableName}] ALTER COLUMN [{columnName}] SMALLINT;',
      },
      TIMEOUT: {
        fix: 'Increase query timeout or optimize table indexes',
        sql: '-- Optimize indexes or partition large tables',
      },
      CONNECTION_ERROR: {
        fix: 'Check network connectivity and firewall rules',
        sql: '-- Verify connection settings',
      },
      OTHER: {
        fix: 'Manual investigation required',
        sql: '-- Review error details',
      },
    };

    return fixes[category] || fixes.OTHER;
  };

  // Extract failed tables with error details
  const failedTables = useMemo(() => {
    const tables: FailedTable[] = [];

    runs.filter(r => r.status === 'Failed').forEach(run => {
      if (run.failedActivities) {
        run.failedActivities.forEach(activity => {
          const tableName = activity.tableName || run.lastTableProcessed || run.parameters?.TableName || 'Unknown';

          const { fix, sql } = getRecommendedFix(activity.errorCategory);

          tables.push({
            tableName,
            errorCategory: activity.errorCategory,
            errorMessage: activity.error?.message || 'Unknown error',
            runId: run.runId,
            runStart: run.runStart,
            recommendedFix: fix,
            sqlFix: sql,
          });
        });
      }
    });

    return tables;
  }, [runs]);

  // Group by error category
  const groupedByCategory = useMemo(() => {
    const grouped: Record<string, FailedTable[]> = {};

    failedTables.forEach(table => {
      if (!grouped[table.errorCategory]) {
        grouped[table.errorCategory] = [];
      }
      grouped[table.errorCategory].push(table);
    });

    return grouped;
  }, [failedTables]);

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      VARCHAR_TOO_SMALL: 'bg-red-100 text-red-800 border-red-300',
      NVARCHAR_TOO_SMALL: 'bg-red-100 text-red-800 border-red-300',
      DECIMAL_PRECISION: 'bg-orange-100 text-orange-800 border-orange-300',
      TINYINT_OVERFLOW: 'bg-orange-100 text-orange-800 border-orange-300',
      TIMEOUT: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      CONNECTION_ERROR: 'bg-blue-100 text-blue-800 border-blue-300',
      OTHER: 'bg-gray-100 text-gray-800 border-gray-300',
    };

    return colors[category] || colors.OTHER;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (failedTables.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          No failed tables found in the selected time range. All pipelines succeeded!
        </AlertDescription>
      </Alert>
    );
  }

  // Calculate unique tables
  const uniqueTables = useMemo(() => {
    const tableNames = new Set(failedTables.map(t => t.tableName));
    return tableNames.size;
  }, [failedTables]);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Failed Tables Summary</CardTitle>
          <CardDescription>
            {failedTables.length} failed activit{failedTables.length !== 1 ? 'ies' : 'y'} across{' '}
            {uniqueTables} unique table{uniqueTables !== 1 ? 's' : ''} in{' '}
            {Object.keys(groupedByCategory).length} error categor{Object.keys(groupedByCategory).length !== 1 ? 'ies' : 'y'}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Grouped by Error Category */}
      {Object.entries(groupedByCategory).map(([category, tables]) => {
        const uniqueTablesInCategory = new Set(tables.map(t => t.tableName)).size;
        return (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge className={getCategoryColor(category)}>
                  {category.replace(/_/g, ' ')}
                </Badge>
                <span className="text-sm font-normal text-muted-foreground">
                  ({tables.length} failure{tables.length !== 1 ? 's' : ''} • {uniqueTablesInCategory} table{uniqueTablesInCategory !== 1 ? 's' : ''})
                </span>
              </CardTitle>
              <CardDescription>{getRecommendedFix(category).fix}</CardDescription>
            </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Table Name</TableHead>
                    <TableHead>Error Message</TableHead>
                    <TableHead className="w-[150px]">Failed At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tables.map((table, idx) => (
                    <TableRow key={`${table.tableName}-${idx}`}>
                      <TableCell className="font-medium align-top">{table.tableName}</TableCell>
                      <TableCell className="align-top">
                        <div className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                          {table.errorMessage}
                        </div>
                      </TableCell>
                      <TableCell className="align-top">{format(new Date(table.runStart), 'MMM d, HH:mm')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
}
